import { useAuth } from '@/hooks/useAuth'
import { invokeMessages, messagesError } from '@/lib/messages'
import type { Dispatch, ReactNode, SetStateAction } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

interface MessagesContextValue {
  unreadCount: number
  refreshUnread: () => Promise<void>
  setUnreadCount: Dispatch<SetStateAction<number>>
}

const MessagesContext = createContext<MessagesContextValue | null>(null)

export function MessagesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const [unreadCount, setUnreadCount] = useState(0)

  const refreshUnread = useCallback(async () => {
    if (!isAuthenticated) {
      setUnreadCount(0)
      return
    }
    try {
      const result = await invokeMessages<{ unreadCount?: number }>({ action: 'unread' })
      setUnreadCount(Number(result?.unreadCount || 0))
    } catch (error) {
      console.error(messagesError(error))
    }
  }, [isAuthenticated])

  useEffect(() => {
    void refreshUnread()
    if (!isAuthenticated) return undefined
    const timer = window.setInterval(() => {
      void refreshUnread()
    }, 20000)
    return () => window.clearInterval(timer)
  }, [isAuthenticated, refreshUnread])

  const value = useMemo(
    () => ({ unreadCount, refreshUnread, setUnreadCount }),
    [unreadCount, refreshUnread],
  )

  return <MessagesContext.Provider value={value}>{children}</MessagesContext.Provider>
}

export function useUnreadMessages() {
  const ctx = useContext(MessagesContext)
  if (!ctx) throw new Error('useUnreadMessages must be used within MessagesProvider')
  return ctx
}
